//import * as Sentry from "@sentry/nextjs";
import domSerializer from "dom-serializer";
import { parseDocument } from "htmlparser2";

import { trackEvent, identifyUser } from "../utils/analytics";
// mixpanel initialization moved to utils/analytics.js

const DEFAULT_API = "https://api.sh0p.in";

/**
 * A host whose first label is the store subdomain: `<store>.sh0p.in` for this
 * storefront, or `<store>.<NEXT_PUBLIC_STORE_HOST_SUFFIX>` on a staging box
 * (e.g. `memehub.staging.example`). Any other host is treated as a custom
 * domain and resolved through the backend.
 */
function isStoreHost(host) {
  if (!host) return false;
  const bare = host.split(":")[0].toLowerCase();
  if (bare === "sh0p.in" || bare.endsWith(".sh0p.in")) return true;
  const suffix = (process.env.NEXT_PUBLIC_STORE_HOST_SUFFIX || "").replace(/^\./, "");
  return Boolean(suffix) && bare.endsWith(`.${suffix}`);
}

const isLocalNetwork = (host) => {
  if (!host) return false;
  return (
    /^192\.168\.\d{1,3}\.\d{1,3}(:\d+)?$/.test(host) ||
    host.startsWith("localhost") ||
    host.startsWith("127.0.0.1")
  );
};

/** Platform storefront hosts: briefly.sh0p.in — not custom domains. */
const isPlatformHost = (host) => {
  if (!host) return false;
  const hostname = String(host).split(":")[0].toLowerCase();
  return hostname === "sh0p.in" || hostname.endsWith(".sh0p.in");
};

const sanitizeHtml = (htmlContent) => {
  try {
    if (!htmlContent) return "";
    const document = parseDocument(htmlContent);
    const fixedHTML = domSerializer(document);
    return fixedHTML;
  } catch (error) {
    console.error("Error fixing HTML:", error);
    return "";
  }
};

const cashfreeEnv = process.env.CASHFREE_ENV;

const environment = process.env.ENV;
const nodeEnv = process.env.NEXT_PUBLIC_NODE_ENV;

function getPublicServerEndpoint() {
  // One API for every store, including custom domains. Local and staging point
  // this env var at their own host; production leaves it as https://api.sh0p.in.
  return (process.env.NEXT_PUBLIC_SERVER_ENDPOINT || DEFAULT_API).replace(/\/$/, "");
}

/**
 * Every host `getPublicServerEndpoint` can resolve to, regardless of which
 * one a given request actually lands on. For code that needs to recognize
 * "this URL is one of our API hosts" without replaying the subdomain/routing
 * logic above — e.g. deciding whether to attach trace headers.
 */
function getAllPublicServerEndpoints() {
  return [getPublicServerEndpoint()];
}

const guid = () => {
  var result, i, j;
  result = "";
  for (j = 0; j < 32; j++) {
    if (j == 8 || j == 12 || j == 16 || j == 20) result = result + "-";
    i = Math.floor(Math.random() * 16)
      .toString(16)
      .toUpperCase();
    result = result + i;
  }
  return result;
};

function isNumeric(str) {
  if (typeof str != "string") return false;
  return !isNaN(str) && !isNaN(parseFloat(str));
}

//Stock check is not actually done through this method now
async function getStockAvailability(cartProducts) {
  if (!cartProducts || !cartProducts.length) return false;
  let isStockAvailable = true;
  return isStockAvailable;
}

function getLuminance(hex) {
  hex = hex.replace(/^#/, "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const compand = (c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const R = compand(r);
  const G = compand(g);
  const B = compand(b);

  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export {
  nodeEnv,
  isLocalNetwork,
  isPlatformHost,
  getPublicServerEndpoint,
  getAllPublicServerEndpoints,
  isStoreHost,
  guid,
  isNumeric,
  environment,
  cashfreeEnv,
  getStockAvailability,
  trackEvent,
  identifyUser,
  sanitizeHtml,
  getLuminance,
};
